import { Loading } from "@/components/common/Loading/Loading"
import { Button } from "@/components/ui/button"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconLoader2 } from "@tabler/icons-react"
import { AlertTriangle, ArrowLeft } from "lucide-react"
import { useEffect, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router"
import { apiErrorMessage, toastApiError } from "../../../api/apiError"
import type { QuestionnaireEditable } from "../../../api/customerRequestsApi"
import {
  buildInitialEditable,
  useDeleteQuestionnaire,
  useQuestionnaireIntake,
  useStartProcessing,
  useStoreQuestionnaire,
} from "../../../api/customerRequestsApi"
import { RADIAL_PROCESSING_GLOW } from "../../master-data/masterDataStyles"
import { SectionLabel } from "../../shared/SectionLabel"
import { StepStrip } from "../../shared/StepStrip"
import { ConfirmStep, type DuplicateOf } from "../ConfirmStep"
import {
  customerRequestDetailPath,
  customerRequestImportPath,
  customerRequestProcessPath,
} from "../routes"

/**
 * Step 2 — fetches the AI-detected intake by id and shows the read-only
 * "what we read" panel beside the editable record fields. Storing persists the
 * request and routes to the stored/processing detail page.
 */
export function CustomerRequestConfirmPage() {
  const { translate } = useTranslation()
  const regulationSettingsOn = useFeatureFlag("MC_REGULATION_SETTINGS")
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  // Set by the upload step when these exact bytes were already uploaded. Route
  // state, not a query param: it is a hint about THIS arrival, so it should not
  // survive a reload or be shareable as part of the link.
  const duplicateOf = (
    useLocation().state as { duplicateOf?: DuplicateOf } | null
  )?.duplicateOf

  const { data: intake, isLoading } = useQuestionnaireIntake(id)
  const { mutate: storeQuestionnaire } = useStoreQuestionnaire()
  const { mutate: startProcessing } = useStartProcessing()
  const { mutate: deleteQuestionnaire } = useDeleteQuestionnaire()

  const [editable, setEditable] = useState<QuestionnaireEditable | null>(null)
  const [storing, setStoring] = useState(false)
  const [storeError, setStoreError] = useState<string | null>(null)

  // Only seed the editable form once extraction is done — otherwise the empty
  // detected_metadata (during `extracting`) would prefill blank fields that
  // the user would have to re-enter after the poll flips.
  useEffect(() => {
    if (intake && intake.status === "needs_confirm") {
      setEditable(buildInitialEditable(intake))
    }
  }, [intake])

  // No id in the URL — send the user back to the upload step.
  useEffect(() => {
    if (!id) navigate(customerRequestImportPath, { replace: true })
  }, [id, navigate])

  const handleStore = () => {
    if (!intake || !editable || !editable.customerId) return
    setStoring(true)
    setStoreError(null)
    storeQuestionnaire(
      {
        draftId: intake.id,
        customerId: editable.customerId,
        fileKey: intake.fileKey,
        fileName: intake.fileName,
        documentType: intake.docType || null,
        customerDueDate: editable.due || null,
        source: "upload",
        frameworks: editable.frameworks,
        // Confirmed regulations as keys, only where the reviewer could pick them;
        // otherwise the backend reads the keys the detected wording names.
        regulationKeys: regulationSettingsOn
          ? editable.regulationKeys
          : undefined,
        articleCodes: editable.articleCodes,
        notes: editable.notes || null,
      },
      {
        onSuccess: (item) => {
          // Skip the intermediate detail page — go straight into processing so
          // the user lands on the mapping step without an extra click.
          startProcessing(item.id, {
            onSuccess: () => {
              setStoring(false)
              navigate(customerRequestProcessPath(item.id, "mapping"))
            },
            onError: (err) => {
              setStoring(false)
              toastApiError(err, translate, "MC_Q_START_PROCESSING_FAILED")
              // Request itself was stored fine — land on the detail page so
              // the user can retry "Start processing" from there.
              navigate(customerRequestDetailPath(item.id))
            },
          })
        },
        onError: (err) => {
          setStoring(false)
          setStoreError(apiErrorMessage(err, translate, "MC_Q_STORE_FAILED"))
          toastApiError(err, translate, "MC_Q_STORE_FAILED")
        },
      },
    )
  }

  // "Back to upload" discards the current draft so it doesn't linger in the
  // inbox as a needs_confirm row, then returns to the upload step.
  const handleBack = () => {
    if (intake) deleteQuestionnaire(intake.id)
    navigate(customerRequestImportPath)
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <IconLoader2 size={22} className="animate-spin text-grey5" />
      </div>
    )
  }

  if (!intake) {
    return (
      <div className="mx-auto w-full max-w-[720px]">
        <div className="mb-3.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(customerRequestImportPath)}
          >
            <ArrowLeft size={14} className="mr-1.5" />
            {translate("MC_Q_RECORD_BTN_BACK")}
          </Button>
        </div>
        <div className="rounded-xl border border-grey4 bg-grey2 p-8 text-center text-[13px] text-grey5">
          {translate("MC_Q_RECORD_NOT_FOUND")}
        </div>
      </div>
    )
  }

  // Async extraction still running — the upload flow returns immediately with
  // status='extracting'; the events poller flips it to needs_confirm (or
  // extraction_failed) once the Lambda finishes. useQuestionnaireIntake polls
  // every 2.5s while status stays 'extracting'.
  if (intake.status === "extracting") {
    return (
      <div className="flex h-full flex-col">
        <StepStrip step={2} total={2} label={translate("MC_Q_CONFIRM_STEP")} />
        <div className="relative flex flex-1 flex-col items-center justify-center overflow-hidden rounded-2xl border border-grey4">
          <div
            className="pointer-events-none absolute inset-0"
            style={RADIAL_PROCESSING_GLOW}
          />
          <div className="relative flex flex-col items-center gap-6">
            <Loading autoIncrementIntervalMs={150} />
            <div className="space-y-1.5 text-center">
              <SectionLabel>{translate("MC_Q_EXTRACTING_TITLE")}</SectionLabel>
              <p className="mx-auto max-w-[420px] text-[13px] text-grey5">
                {translate("MC_Q_EXTRACTING_SUBTITLE")}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Lambda emitted MC.Questionnaire.ExtractFailed — the row is a dead-end
  // in this state; back button discards the draft and returns to upload.
  if (intake.status === "extraction_failed") {
    return (
      <div className="mx-auto w-full max-w-[640px]">
        <StepStrip step={2} total={2} label={translate("MC_Q_CONFIRM_STEP")} />
        <div className="flex min-h-[420px] flex-col items-center justify-center rounded-2xl border border-grey4 bg-grey1">
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="text-red5 flex h-12 w-12 items-center justify-center rounded-full bg-red1">
              <AlertTriangle size={24} />
            </div>
            <div className="space-y-1.5">
              <SectionLabel>
                {translate("MC_Q_EXTRACTION_FAILED_TITLE")}
              </SectionLabel>
              <p className="mx-auto max-w-[420px] text-[13px] text-grey5">
                {translate("MC_Q_EXTRACTION_FAILED_SUBTITLE")}
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={handleBack}>
              <ArrowLeft size={14} className="mr-1.5" />
              {translate("MC_Q_EXTRACTION_FAILED_RETRY")}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // needs_confirm — extraction done, show the editable confirm form.
  if (!editable) {
    // Poll landed on needs_confirm but the seed-editable effect hasn't run
    // yet this render — show the spinner one more tick.
    return (
      <div className="flex h-full items-center justify-center">
        <IconLoader2 size={22} className="animate-spin text-grey5" />
      </div>
    )
  }

  return (
    <ConfirmStep
      intake={intake}
      editable={editable}
      setEditable={(e) => setEditable(e)}
      onBack={handleBack}
      onStore={handleStore}
      storing={storing}
      storeError={storeError}
      duplicateOf={duplicateOf}
    />
  )
}
